import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  LinearProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Paper,
  Alert,
  Grid,
  Chip,
  Tabs,
  Tab,
  Tooltip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as CloudUploadIcon,
  Folder as FolderIcon,
  InsertDriveFile as InsertDriveFileIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Delete as DeleteIcon,
  Pause as PauseIcon,
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Info as InfoIcon,
  Upload as UploadIcon,
  Remove as MinimizeIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useSnackbar } from 'notistack';
import { useChunkedUpload } from '../../hooks/useChunkedUpload';
import { folderService } from '../../services/folderService';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store';
import { setUploadDialogMinimized } from '../../store/slices/uiSlice';
import { 
  uploadFileBackground,
  uploadBatchBackground,
  getRetainedUploadFile,
  selectUploads,
  cancelUpload as cancelBackgroundUpload,
  cancelAllUploads,
  clearCompletedUploads,
  removeUpload,
  type UploadItem
} from '../../store/slices/uploadSlice';
import { v4 as uuidv4 } from 'uuid';
import api from '../../utils/api';
import { checkBatchStorageQuota } from '../../utils/uploadValidation';

interface FileUploadProps {
  open: boolean;
  onClose: () => void;
  folderId?: string;
  onUploadComplete?: () => void;
  className?: string;
  style?: React.CSSProperties;
  maxFileSize?: number;
  allowedFileTypes?: string[];
}

type UploadStatus = 'pending' | 'uploading' | 'completed' | 'error';

interface UploadProgress {
  progress: number;
  status: UploadStatus;
  error?: string;
}

interface UploadingFile {
  id: string;
  file: File;
  path?: string;
  status: UploadStatus;
  progress: UploadProgress;
  error?: string;
  uploadId?: string; // Background upload ID
  batchId?: string; // Batch/session ID this file belongs to
}

interface UploadBatch {
  id: string;
  name: string;
  files: string[]; // File IDs in this batch
  totalFiles: number;
  completedFiles: number;
  failedFiles: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  startTime: number;
  progress: number; // Overall progress 0-100
}

const BG_STATUS_RANK: Record<UploadItem['status'], number> = {
  completed: 5,
  processing: 4,
  uploading: 3,
  pending: 2,
  paused: 1,
  error: 0,
  cancelled: -1,
  cancelling: -1
};

function compareBackgroundUploadRank(a: UploadItem, b: UploadItem): number {
  const dr = (BG_STATUS_RANK[a.status] ?? 0) - (BG_STATUS_RANK[b.status] ?? 0);
  if (dr !== 0) return dr;
  return (a.startTime ?? 0) - (b.startTime ?? 0);
}

function pickBestBackgroundUpload(file: UploadingFile, uploads: UploadItem[]): UploadItem | undefined {
  const byClient = uploads.filter(u => u.clientFileId === file.id);
  if (byClient.length > 0) {
    return [...byClient].sort(compareBackgroundUploadRank)[byClient.length - 1];
  }
  const legacyActive = uploads.filter(
    u =>
      !u.clientFileId &&
      u.name === file.file.name &&
      u.size === file.file.size &&
      (u.status === 'pending' || u.status === 'uploading' || u.status === 'processing')
  );
  if (legacyActive.length > 0) {
    return [...legacyActive].sort(compareBackgroundUploadRank)[legacyActive.length - 1];
  }
  return undefined;
}

function mapBgStatusToLocal(bg: UploadItem): UploadStatus {
  switch (bg.status) {
    case 'completed':
      return 'completed';
    case 'error':
      return 'error';
    case 'uploading':
    case 'processing':
      return 'uploading';
    case 'cancelled':
      return 'error';
    default:
      return 'pending';
  }
}

  const getStatusIcon = (file: UploadingFile) => {
    switch (file.status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'uploading':
        return <CloudUploadIcon color="primary" />;
      default:
        return <InsertDriveFileIcon color="action" />;
    }
  };

// Preserve unchanged rows while other transfers publish progress.
const UploadFileRow = React.memo(({file, upload, sizeLabel, onRemove, onRetry}: {
  file: UploadingFile; upload?: UploadItem; sizeLabel: string;
  onRemove: (id: string) => void; onRetry: (id: string) => void;
}) => (
                  <ListItem key={file.id}>
                    <ListItemIcon>
                      {getStatusIcon(file)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                            {file.file.name}
                          </Typography>
                          <Chip
                            label={sizeLabel}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            {file.status === 'uploading' ? 'Uploading to background...' :
                             file.status === 'completed' ? 'Upload completed' :
                             file.status === 'error' ? 'Upload failed' :
                             file.status === 'pending' ? 'Waiting to upload...' : file.status}
                            {file.error && (
                              <span style={{ color: '#d32f2f', display: 'block', marginTop: 4 }}>
                                Error: {file.error}
                              </span>
                            )}
                            {file.path && (
                              <span style={{ display: 'block', marginTop: 4 }}>
                                Path: {file.path}
                              </span>
                            )}
                            {file.uploadId && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                ID: {file.uploadId.substring(0, 8)}...
                              </Typography>
                            )}
                          </Typography>
                          {file.status === 'uploading' && file.uploadId && (
                            <Box mt={0.5}>
                              {(() => {
                                const bgUpload = upload;
                                if (bgUpload) {
                                  return (
                                    <>
                                      <LinearProgress
                                        variant="determinate"
                                        value={bgUpload.progress}
                                        sx={{ height: 4, borderRadius: 2 }}
                                      />
                                      <Typography variant="caption" color="textSecondary">
                                        {bgUpload.progress}% • {bgUpload.speed ? `${(bgUpload.speed / 1024 / 1024).toFixed(2)} MB/s` : ''}
                                      </Typography>
                                    </>
                                  );
                                }
                                return null;
                              })()}
                            </Box>
                          )}
                        </Box>
                      }
                    />
                    <Box display="flex" gap={1}>
                      {file.status === 'error' && (
                        <IconButton
                          size="small"
                          onClick={() => onRetry(file.id)}
                          title="Retry upload"
                          color="primary"
                        >
                          <PlayArrowIcon />
                        </IconButton>
                      )}
                      {file.status === 'completed' && (
                        <IconButton
                          size="small"
                          onClick={() => onRemove(file.id)}
                          title="Remove from list"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                      {(file.status === 'pending' || file.status === 'uploading') && (
                        <IconButton
                          size="small"
                          onClick={() => onRemove(file.id)}
                          title="Cancel upload"
                          color="error"
                        >
                          <StopIcon />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
));

const SimpleFolderUploadDialog: React.FC<FileUploadProps> = ({ 
  open, 
  onClose, 
  folderId, 
  onUploadComplete,
  className,
  style,
  maxFileSize = 10 * 1024 * 1024 * 1024, // 10GB default
  allowedFileTypes = [] // Empty array means allow all file types
}) => {
  const [files, setFiles] = useState<UploadingFile[]>([]);
  const filesRef = useRef(files);
  filesRef.current = files;
  const retryRef = useRef<(id: string) => void>(() => {});
  const retryQueuedFile = useCallback((id: string) => retryRef.current(id), []);
  const [filePage, setFilePage] = useState(0);
  const [backgroundPage, setBackgroundPage] = useState(0);
  useEffect(() => setFilePage(page => Math.min(page, Math.max(0, Math.ceil(files.length / 100) - 1))), [files.length]);
  const [uploadBatches, setUploadBatches] = useState<Map<string, UploadBatch>>(new Map());
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [useBackgroundUpload, setUseBackgroundUpload] = useState(false);
  const [uploadType, setUploadType] = useState<'files' | 'folders'>('files');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const folderInputRef = useRef<HTMLInputElement>(null);

  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<AppDispatch>();
  const uploadDialogMinimized = useSelector((state: RootState) => state.ui.uploadDialogMinimized);
  
  // Get background upload progress
  const backgroundUploads = useSelector((state: RootState) => selectUploads(state));
  const fileByUploadId = useMemo(() => new Map(files.filter(file => file.uploadId).map(file => [file.uploadId!, file])), [files]);
  const displayedBackgroundUploads = useMemo(() => backgroundUploads.filter(upload => !fileByUploadId.has(upload.id) && !['cancelled','cancelling'].includes(upload.status)), [backgroundUploads, fileByUploadId]);
  useEffect(() => setBackgroundPage(page => Math.min(page, Math.max(0, Math.ceil(displayedBackgroundUploads.length / 100) - 1))), [displayedBackgroundUploads.length]);
  const backgroundById = useMemo(() => new Map(backgroundUploads.map(upload => [upload.id, upload])), [backgroundUploads]);
  const backgroundByClientId = useMemo(() => new Map(backgroundUploads.filter(upload => upload.clientFileId).map(upload => [upload.clientFileId!, upload])), [backgroundUploads]);

  useEffect(() => {
    if (!open) {
      dispatch(setUploadDialogMinimized(false));
    }
  }, [open, dispatch]);

  // Use the chunked upload hook
  const {
    state: uploadState,
    startUpload,
    pauseUpload,
    resumeUpload,
    cancelUpload: cancelChunkedUpload
  } = useChunkedUpload({
    onProgress: (progress) => {
      console.log('[CHUNKED UPLOAD] Overall progress:', progress);
    },
    onComplete: (result) => {
      console.log('[CHUNKED UPLOAD] ✅ Upload completed:', result);
      enqueueSnackbar(`Upload completed: ${result.successfulUploads} successful, ${result.failedUploads} failed`, {
        variant: result.failedUploads === 0 ? 'success' : 'warning'
      });
      onUploadComplete?.();
    },
    onError: (error) => {
      console.error('[CHUNKED UPLOAD] ❌ Upload error:', error);
      setError(error);
    },
    onFileProgress: (fileId, progress, details) => {
      console.log(`[CHUNKED UPLOAD] File progress: fileId=${fileId}, progress=${progress}%, loaded=${details?.loaded}, total=${details?.total}`);
      setFiles(prevFiles => 
        prevFiles.map(f => 
          f.id === fileId 
            ? { 
                ...f, 
                status: 'uploading',
                progress: { 
                  ...f.progress, 
                  progress: progress,
                  status: 'uploading'
                }
              }
            : f
        )
      );
    },
    onFileComplete: (fileId, success, error) => {
      console.log(`[CHUNKED UPLOAD] File completed: fileId=${fileId}, success=${success}, error=${error}`);
      setFiles(prevFiles => 
        prevFiles.map(f => 
          f.id === fileId 
            ? { 
                ...f, 
                status: success ? 'completed' : 'error',
                progress: { 
                  ...f.progress, 
                  status: success ? 'completed' : 'error',
                  progress: success ? 100 : 0
                },
                error: error || undefined
              }
            : f
        )
      );
    }
  });

  const wasOpenRef = useRef(false);
  // Reset state only when the dialog is opened fresh (not when returning from minimized)
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setFiles([]);
      setError(null);
      setIsProcessing(false);
    }
    wasOpenRef.current = open;
  }, [open]);

  // Each row has an exact task ID; synchronize without nested state updates or full-list searches.
  useEffect(() => {
    const synchronized = files.map(file => {
      const upload = file.uploadId ? backgroundById.get(file.uploadId) : backgroundByClientId.get(file.id);
      if (!upload) return file;
      const status = mapBgStatusToLocal(upload);
      if (file.uploadId === upload.id && file.status === status && file.progress.progress === upload.progress && file.error === upload.error) return file;
      return {...file, uploadId: upload.id, status, error: upload.error,
        progress: {...file.progress, progress: upload.progress, status, error: upload.error}};
    });
    if (synchronized.some((file,index) => file !== files[index])) setFiles(synchronized);
    const groups = new Map<string, UploadingFile[]>();
    synchronized.forEach(file => {if (file.batchId) {const group = groups.get(file.batchId) || []; group.push(file); groups.set(file.batchId, group);}});
    setUploadBatches(previous => {
      const updated = new Map(previous); let changed = false;
      groups.forEach((rows,id) => {
        const batch = previous.get(id); if (!batch) return;
        const live = rows.filter(file => !['cancelled','cancelling'].includes(backgroundById.get(file.uploadId || '')?.status || ''));
        const completedFiles = live.filter(file => file.status === 'completed').length;
        const failedFiles = live.filter(file => file.status === 'error').length;
        const totalBytes = live.reduce((sum,file) => sum + file.file.size,0);
        const progress = totalBytes ? Math.round(live.reduce((sum,file) => sum + file.file.size * file.progress.progress,0) / totalBytes) : 0;
        const status: UploadBatch['status'] = completedFiles + failedFiles === live.length ? (failedFiles ? 'error' : 'completed')
          : live.some(file => backgroundById.get(file.uploadId || '')?.status === 'processing') ? 'processing'
          : live.some(file => file.status === 'uploading') ? 'uploading' : 'pending';
        if (batch.completedFiles !== completedFiles || batch.failedFiles !== failedFiles || batch.progress !== progress || batch.status !== status || batch.totalFiles !== live.length) {
          updated.set(id,{...batch,completedFiles,failedFiles,progress,status,totalFiles:live.length}); changed = true;
        }
      });
      return changed ? updated : previous;
    });
  }, [backgroundById, backgroundByClientId, files.length]);

  // Update isProcessing based on active batches and uploads
  useEffect(() => {
    const activeBatchesArray = Array.from(uploadBatches.values()).filter(batch => {
      if (batch.status === 'completed' || batch.status === 'error') return false;
      const batchFiles = files.filter(f => f.batchId === batch.id);
      return batchFiles.some(f => {
        if (f.status === 'uploading' || f.status === 'pending') return true;
        if (f.uploadId) {
          const bgUpload = backgroundById.get(f.uploadId!);
          if (bgUpload && (bgUpload.status === 'uploading' || bgUpload.status === 'processing')) {
            return true;
          }
        }
        return false;
      });
    });
    
    const hasActiveFiles = files.some(f => {
      if (!f.batchId && (f.status === 'uploading' || f.status === 'pending')) return true;
      if (f.uploadId && !f.batchId) {
        const bgUpload = backgroundById.get(f.uploadId!);
        if (bgUpload && (bgUpload.status === 'uploading' || bgUpload.status === 'processing')) {
          return true;
        }
      }
      return false;
    });
    
    const hasActiveBackground = backgroundUploads.some(u => {
      const isTracked = fileByUploadId.has(u.id);
      if (!isTracked && (u.status === 'uploading' || u.status === 'pending' || u.status === 'processing')) {
        return true;
      }
      return false;
    });
    
    const hasActive = activeBatchesArray.length > 0 || hasActiveFiles || hasActiveBackground || uploadState.isUploading;
    
    if (!hasActive && isProcessing) {
      setIsProcessing(false);
    } else if (hasActive && !isProcessing) {
      setIsProcessing(true);
    }
  }, [uploadBatches, files, backgroundUploads, fileByUploadId, uploadState.isUploading, isProcessing]);

  // Monitor and close dialog when all uploads are complete
  useEffect(() => {
    // Only check if we have files that were uploaded to background
    const filesWithUploadIds = files.filter(f => f.uploadId);
    
    // Don't close if we're still processing (initiating uploads)
    if (isProcessing) return;

    // Check chunked upload state - if it's uploading, don't close
    if (uploadState.isUploading) return;

    // If no files with uploadIds, check if we have any files at all
    if (filesWithUploadIds.length === 0) {
      // If we have files but none have uploadIds, they might be pending
      if (files.length > 0 && files.some(f => f.status === 'pending' || f.status === 'uploading')) {
        return; // Still have pending/uploading files
      }
      return; // No files to track
    }

    // Get tracked upload IDs
    const trackedUploadIds = new Set(filesWithUploadIds.map(f => f.uploadId).filter(Boolean) as string[]);
    if (trackedUploadIds.size === 0) return;

    // Check if all tracked background uploads exist and are complete
    const trackedUploads = backgroundUploads.filter(u => trackedUploadIds.has(u.id));
    
    // If we have fewer uploads than expected, some haven't started yet
    if (trackedUploads.length < trackedUploadIds.size) return;

    // Check if all tracked uploads are complete (completed or error)
    const allBackgroundComplete = trackedUploads.every(u => 
      u.status === 'completed' || u.status === 'error'
    );

    // Also verify all local files with uploadIds are marked as complete
    const allLocalFilesComplete = filesWithUploadIds.every(file => 
      file.status === 'completed' || file.status === 'error'
    );

    // Check if any files without uploadIds are still pending/uploading
    const filesWithoutUploadIds = files.filter(f => !f.uploadId);
    const allOtherFilesComplete = filesWithoutUploadIds.every(file =>
      file.status === 'completed' || file.status === 'error'
    );

    if (allBackgroundComplete && allLocalFilesComplete && allOtherFilesComplete) {
      const hasErrors =
        trackedUploads.some(u => u.status === 'error') ||
        filesWithUploadIds.some(f => f.status === 'error');
      if (hasErrors) {
        return;
      }
      // All uploads succeeded — close dialog after a short delay
      const timer = setTimeout(() => {
        onUploadComplete?.();
        onClose();
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [files, backgroundUploads, uploadState.isUploading, isProcessing, onClose, onUploadComplete]);

  const handleClose = () => {
    const hasActiveUploads = uploadState.isUploading || 
                             isProcessing ||
                             files.some(f => f.status === 'uploading' || f.status === 'pending') ||
                             backgroundUploads.some(u => u.status === 'uploading' || u.status === 'pending' || u.status === 'processing');
    
    if (!hasActiveUploads) {
      onClose();
    } else {
      enqueueSnackbar('Please wait for uploads to complete or cancel them first', { variant: 'warning' });
    }
  };

  const validateFile = (file: File): string | null => {
    if (file.size > maxFileSize) {
      return `File size exceeds ${formatFileSize(maxFileSize)} limit`;
    }
    return null;
  };

  // Process files in chunks to prevent browser hanging
  const processFilesInChunks = (fileList: File[], chunkSize: number = 100) => {
    const chunks: File[][] = [];
    for (let i = 0; i < fileList.length; i += chunkSize) {
      chunks.push(fileList.slice(i, i + chunkSize));
    }

    let currentChunk = 0;
    const processChunk = () => {
      if (currentChunk >= chunks.length) {
        enqueueSnackbar(`Discovered ${fileList.length} files — ready to queue`, { variant: 'info' });
        return;
      }

      const chunk = chunks[currentChunk];
      const filesToAdd = chunk.map(file => {
        const validationError = validateFile(file);
        return {
          id: uuidv4(),
          file,
          path: (file as any).webkitRelativePath || undefined,
          progress: { progress: 0, status: (validationError ? 'error' : 'pending') as UploadStatus },
          status: (validationError ? 'error' : 'pending') as UploadStatus,
          error: validationError || undefined
        } as UploadingFile;
      });

      setFiles(prev => [...prev, ...filesToAdd]);

      // Show validation errors
      filesToAdd.forEach(fileItem => {
        if (fileItem.error) {
          enqueueSnackbar(`${fileItem.file.name}: ${fileItem.error}`, { 
            variant: 'error',
            autoHideDuration: 3000
          });
        }
      });

      currentChunk++;
      
      // Process next chunk after a small delay to prevent UI blocking
      setTimeout(processChunk, 10);
    };

    processChunk();
  };

  // Handle file drop
  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[], event: any) => {
    console.log('Dropzone onDrop triggered with files:', acceptedFiles.length);
    
    if (acceptedFiles.length === 0) return;

    // Check if this is a folder drop (files have webkitRelativePath)
    const hasFolderStructure = acceptedFiles.some(file => (file as any).webkitRelativePath);
    
    // Debug: Show the first few files and their paths
    console.log('First 5 files and their webkitRelativePath:');
    acceptedFiles.slice(0, 5).forEach((file, index) => {
      console.log(`File ${index}: ${file.name}, webkitRelativePath: ${(file as any).webkitRelativePath}`);
    });
    
    if (hasFolderStructure) {
      // This is a folder drop
      if (acceptedFiles.length > 1000) {
        enqueueSnackbar(`Processing folder with ${acceptedFiles.length} files... This may take a moment.`, {
          variant: 'info',
          autoHideDuration: 3000
        });
      }
    } else {
      // This is individual files
      if (acceptedFiles.length > 100) {
        enqueueSnackbar(`Processing ${acceptedFiles.length} files... This may take a moment.`, {
          variant: 'info',
          autoHideDuration: 3000
        });
      }
    }

    // Process files in chunks to prevent browser hanging
    processFilesInChunks(acceptedFiles);
  }, [enqueueSnackbar]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    noClick: true, // Disable click to prevent native file picker
    noKeyboard: true,
    useFsAccessApi: false,
    preventDropOnDocument: true
  });

  const handleUploadTypeChange = (event: React.SyntheticEvent, newValue: 'files' | 'folders') => {
    setUploadType(newValue);
    // Clear files when switching types
    if (files.length > 0) {
      setFiles([]);
    }
  };

  const handleFileSelectClick = () => {
    if (uploadType === 'files') {
      fileInputRef.current?.click();
    } else {
      folderInputRef.current?.click();
    }
  };

  const handleUpload = async (forceBackgroundUpload?: boolean) => {
    setError(null);
    const selected = files.filter(file => file.status === 'pending' || file.status === 'error');
    if (!selected.length) return;
    return handleBackgroundUpload(selected);
  };

  const handleBackgroundUpload = async (selected: UploadingFile[]) => {
    const batchId = crypto.randomUUID();
    const ids = new Set(selected.map(file => file.id));
    setFiles(previous => previous.map(file => ids.has(file.id) ? {...file, uploadId: file.id, batchId} : file));
    setUploadBatches(previous => new Map(previous).set(batchId, {id: batchId, name: `Upload ${selected.length} files`,
      files: [...ids], totalFiles: selected.length, completedFiles: 0, failedFiles: 0, status: 'uploading', startTime: Date.now(), progress: 0}));
    setIsProcessing(true);
    const {count: successful, failed, cancelled} = await dispatch(uploadBatchBackground(selected.map(file => ({
      file: file.file, folderId: folderId || undefined, clientFileId: file.id, uploadId: file.id,
      batchId, webkitRelativePath: file.path || file.file.webkitRelativePath
    })))).unwrap();
    enqueueSnackbar(`Upload finished: ${successful} completed, ${failed} failed, ${cancelled} canceled`, {variant: failed || cancelled ? 'warning' : 'success'});
    setIsProcessing(false);
    onUploadComplete?.();
  };

  let folderMap: Map<string, string> | undefined = undefined;

  const removeFile = useCallback((id: string) => {
    const file = filesRef.current.find(f => f.id === id);
    if (file?.uploadId) dispatch(removeUpload(file.uploadId));
    setFiles(previous => previous.filter(file => file.id !== id));
  }, [dispatch]);

  const retryFile = async (id: string) => {
    const file = files.find(f => f.id === id);
    if (!file) return;

    try {
      const oldUploadId = file.uploadId;
      const bgUpload = oldUploadId ? backgroundById.get(oldUploadId!) : undefined;

      if (oldUploadId) {
        dispatch(removeUpload(oldUploadId));
      }

      setFiles(prev => prev.map(f =>
        f.id === id
          ? {
              ...f,
              uploadId: undefined,
              status: 'pending' as UploadStatus,
              progress: {
                ...f.progress,
                status: 'pending' as UploadStatus,
                progress: 0,
                error: undefined
              },
              error: undefined
            }
          : f
      ));

      if (oldUploadId || bgUpload) {
        const retryFileRef = (oldUploadId ? getRetainedUploadFile(oldUploadId) : undefined) || file.file;
        dispatch(
          uploadFileBackground({
            file: retryFileRef,
            folderId: bgUpload?.folderId || folderId,
            clientFileId: file.id,
            webkitRelativePath: file.path
          })
        );
      }
    } catch (error: any) {
      console.error('Error retrying upload:', error);
      enqueueSnackbar(`Failed to retry upload: ${error.message}`, { variant: 'error' });
    }
  };

  retryRef.current = retryFile;

  const retryAllFailedFiles = async () => {
    const failedFiles = files.filter(f => f.status === 'error');
    if (failedFiles.length === 0) return;

    try {
      for (const file of failedFiles) {
        await retryFile(file.id);
      }

      enqueueSnackbar(`Retrying ${failedFiles.length} failed file(s)...`, { variant: 'info' });
    } catch (error: any) {
      console.error('Error retrying all failed files:', error);
      enqueueSnackbar(`Failed to retry some files: ${error.message}`, { variant: 'error' });
    }
  };

  const clearLogs = () => {
    dispatch(clearCompletedUploads());
    setFiles(prev => prev.filter(f => 
      f.status !== 'completed' && f.status !== 'error'
    ));
    enqueueSnackbar('Cleared completed uploads', { variant: 'success' });
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleUploadClick = (forceBackgroundUpload?: boolean) => {
    if (uploadState.isUploading) {
      if (uploadState.isPaused) {
        resumeUpload();
      } else {
        pauseUpload();
      }
    } else {
      // Pass forceBackgroundUpload directly to handleUpload to avoid state timing issues
      handleUpload(forceBackgroundUpload);
    }
  };

  const handleCancelClick = async () => {
    try {
      // Cancel chunked upload
      cancelChunkedUpload();
      
      // Cancel all background uploads for files with uploadIds
      const filesToCancel = files.filter(f => f.uploadId);
      const cancelPromises = filesToCancel.map(file => {
        if (file.uploadId) {
          return dispatch(cancelBackgroundUpload(file.uploadId));
        }
        return Promise.resolve();
      });
      
      // Cancel all background uploads (including ones not tracked in files)
      await Promise.all([...cancelPromises, dispatch(cancelAllUploads())]);
      
      // Update all files to cancelled/error status
      setFiles(prev => prev.map(f => {
        const isActive = f.status === 'uploading' || f.status === 'pending';
        return {
          ...f,
          status: isActive ? 'error' as UploadStatus : f.status,
          error: isActive ? 'Cancelled by user' : f.error,
          progress: isActive ? { ...f.progress, progress: 0, status: 'error' as UploadStatus } : f.progress
        };
      }));
      
      enqueueSnackbar(`Cancelled ${filesToCancel.length} upload(s)`, { variant: 'info' });
    } catch (error: any) {
      console.error('Error cancelling uploads:', error);
      enqueueSnackbar('Error cancelling some uploads', { variant: 'error' });
    }
  };

  /**
   * Combined stats + overall progress.
   * Overall % is byte-weighted (same idea as the upload worker). Chunked-only batches use the worker bar as source of truth to avoid double-counting.
   */
  const calculateCombinedProgress = () => {
    const backgroundById = new Map(backgroundUploads.map((u) => [u.id, u]));
    const includedFiles = files.filter(file => !['cancelled','cancelling'].includes(backgroundById.get(file.uploadId || '')?.status || ''));
    const filesWithUploadIds = includedFiles.filter(f => f.uploadId);
    const filesWithoutUploadIds = includedFiles.filter(f => !f.uploadId);

    let localPending = 0;
    let localUploading = 0;
    let localCompleted = 0;
    let localFailed = 0;

    filesWithUploadIds.forEach(file => {
      const bgUpload = file.uploadId ? backgroundById.get(file.uploadId) : undefined;
      if (bgUpload) {
        if (bgUpload.status === 'completed') localCompleted++;
        else if (bgUpload.status === 'error') localFailed++;
        else if (bgUpload.status === 'uploading' || bgUpload.status === 'processing') localUploading++;
        else localPending++;
      } else {
        if (file.status === 'uploading') localUploading++;
        else if (file.status === 'completed') localCompleted++;
        else if (file.status === 'error') localFailed++;
        else localPending++;
      }
    });

    filesWithoutUploadIds.forEach(file => {
      if (file.status === 'completed') localCompleted++;
      else if (file.status === 'error') localFailed++;
      else if (file.status === 'uploading') localUploading++;
      else localPending++;
    });

    const trackedUploadIds = new Set(filesWithUploadIds.map(f => f.uploadId).filter(Boolean) as string[]);
    const untrackedBackgroundUploads = backgroundUploads.filter(u => !trackedUploadIds.has(u.id) && !['cancelled','cancelling'].includes(u.status));

    const bgPending = untrackedBackgroundUploads.filter(u => u.status === 'pending').length;
    const bgUploading = untrackedBackgroundUploads.filter(u => u.status === 'uploading' || u.status === 'processing').length;
    const bgCompleted = untrackedBackgroundUploads.filter(u => u.status === 'completed').length;
    const bgFailed = untrackedBackgroundUploads.filter(u => u.status === 'error').length;

    const totalPending = localPending + bgPending;
    const totalUploading = localUploading + bgUploading;
    const totalCompleted = localCompleted + bgCompleted;
    const totalFailed = localFailed + bgFailed;
    const totalFiles = Math.max(
      includedFiles.length + untrackedBackgroundUploads.length,
      uploadState.isUploading && uploadState.progress ? uploadState.progress.totalFiles : 0
    );

    const addBytes = (sumBytes: number, sumLoaded: number, size: number, fraction01: number) => {
      const sz = Math.max(0, size);
      if (sz === 0) return { sumBytes, sumLoaded };
      const frac = Math.min(1, Math.max(0, fraction01));
      return { sumBytes: sumBytes + sz, sumLoaded: sumLoaded + sz * frac };
    };

    let sumBytes = 0;
    let sumLoaded = 0;

    includedFiles.forEach(f => {
      const sz = f.file.size || 0;
      if (f.uploadId) {
        const bg = backgroundById.get(f.uploadId);
        if (bg) {
          if (bg.status === 'completed') ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, 1));
          else if (bg.status === 'error') return;
          else ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, (bg.progress ?? 0) / 100));
        } else ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, 0));
        return;
      }
      if (f.status === 'completed') ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, 1));
      else if (f.status === 'error') return;
      else ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, (f.progress?.progress ?? 0) / 100));
    });

    untrackedBackgroundUploads.forEach(u => {
      const sz = u.size || 0;
      if (u.status === 'completed') ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, 1));
      else if (u.status === 'error') return;
      else ({ sumBytes, sumLoaded } = addBytes(sumBytes, sumLoaded, sz, (u.progress ?? 0) / 100));
    });

    let combinedProgress =
      sumBytes > 0 ? Math.round((sumLoaded / sumBytes) * 100) : 0;

    const chunked = uploadState.progress;
    const chunkedOnly =
      uploadState.isUploading &&
      chunked &&
      chunked.totalFiles > 0 &&
      files.length === chunked.totalFiles &&
      files.every(f => !f.uploadId);

    if (chunkedOnly) {
      combinedProgress = Math.min(100, Math.max(0, chunked.progress ?? 0));
    }

    return {
      totalCancelled:backgroundUploads.filter(row=>row.status==='cancelled').length,
      totalPending,
      totalUploading,
      totalCompleted,
      totalFailed,
      totalFiles,
      combinedProgress,
      localPending,
      localUploading,
      localCompleted,
      localFailed,
      bgPending,
      bgUploading,
      bgCompleted,
      bgFailed
    };
  };

  const combinedStats = calculateCombinedProgress();
  
  const pendingCount = combinedStats.totalPending;
  const uploadingCount = combinedStats.totalUploading;
  const completedCount = combinedStats.totalCompleted;
  const errorCount = combinedStats.totalFailed;

  const canMinimize =
    files.length > 0 ||
    uploadState.isUploading ||
    isProcessing ||
    backgroundUploads.length > 0;

  const isChunkedWorkerOnly =
    uploadState.isUploading &&
    uploadState.progress &&
    uploadState.progress.totalFiles > 0 &&
    files.length === uploadState.progress.totalFiles &&
    files.every(f => !f.uploadId);

  return (
    <Dialog 
      open={open && !uploadDialogMinimized} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      className={className}
      style={style}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
          <Typography variant="h6" sx={{ flex: 1, minWidth: 0 }}>
            {uploadState.isUploading 
              ? `Uploading ${uploadType === 'files' ? 'Files' : 'Folder'}...` 
              : uploadType === 'files' 
                ? 'Upload Files' 
                : 'Upload Folder'}
          </Typography>
          <Tooltip title="Minimize to sidebar">
            <span>
              <IconButton
                onClick={() => dispatch(setUploadDialogMinimized(true))}
                disabled={!canMinimize}
                size="small"
                aria-label="Minimize upload dialog"
              >
                <MinimizeIcon />
              </IconButton>
            </span>
          </Tooltip>
          <IconButton onClick={handleClose} disabled={uploadState.isUploading || isProcessing} aria-label="Close dialog">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Upload Type Selection */}
        <Box sx={{ mb: 2 }}>
          <Tabs
            value={uploadType}
            onChange={handleUploadTypeChange}
            variant="fullWidth"
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                minHeight: 48
              }
            }}
          >
            <Tab 
              icon={<InsertDriveFileIcon />} 
              iconPosition="start"
              label="Upload Files" 
              value="files"
              disabled={isProcessing || uploadState.isUploading}
            />
            <Tab 
              icon={<FolderIcon />} 
              iconPosition="start"
              label="Upload Folder" 
              value="folders"
              disabled={isProcessing || uploadState.isUploading}
            />
          </Tabs>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            {uploadType === 'files' ? (
              <>
                <strong>Upload Files:</strong> Select multiple files or drag and drop them here.
              </>
            ) : (
              <>
                <strong>Upload Folder:</strong> To avoid browser hanging with large folders, drag and drop entire folders from your computer. Do not click to browse.
              </>
            )}
          </Typography>
        </Alert>

        {/* Combined Upload Progress */}
        {(files.length > 0 || backgroundUploads.length > 0 || uploadState.isUploading) && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'rgba(25, 118, 210, 0.08)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
              {files.length > 100 && <Box sx={{display:'flex', gap:1}}>
              <Button disabled={filePage === 0} onClick={() => setFilePage(page => page - 1)}>Previous files</Button>
              <Typography>Page {filePage + 1} of {Math.ceil(files.length / 100)}</Typography>
              <Button disabled={(filePage + 1) * 100 >= files.length} onClick={() => setFilePage(page => page + 1)}>Next files</Button>
            </Box>}
            <Typography variant="subtitle1" fontWeight="bold">
                Combined Upload Progress
              </Typography>
              <Button
                size="small"
                onClick={clearLogs}
                disabled={combinedStats.totalCompleted === 0 && combinedStats.totalFailed === 0}
                variant="outlined"
              >
                Clear Logs
              </Button>
            </Box>
            
            {/* Combined Progress Bar */}
            {combinedStats.totalFiles > 0 && (
              <>
                <LinearProgress 
                  variant="determinate" 
                  value={combinedStats.combinedProgress} 
                  sx={{ 
                    mb: 1, 
                    height: 8, 
                    borderRadius: 4,
                    bgcolor: 'rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  {combinedStats.combinedProgress}% overall
                  {combinedStats.totalFiles > 0 && (
                    <>
                      {' '}
                      · {combinedStats.totalCompleted} completed, {combinedStats.totalFailed} failed,{' '}
                      {combinedStats.totalCancelled} canceled, {combinedStats.totalPending} queued, {combinedStats.totalUploading} uploading (
                      {combinedStats.totalFiles} {combinedStats.totalFiles === 1 ? 'file' : 'files'})
                    </>
                  )}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  Progress is weighted by file size so large files count proportionally (matches folder upload worker).
                </Typography>
              </>
            )}
            
            {/* Chunked worker detail — hidden when it matches the combined bar (same byte-weighted value) */}
            {uploadState.progress && uploadState.isUploading && !isChunkedWorkerOnly && (
              <Box mt={2} pt={2} borderTop="1px solid rgba(0, 0, 0, 0.1)">
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  Chunked Upload Progress:
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={uploadState.progress.progress} 
                  sx={{ mb: 0.5, height: 6 }}
                />
                <Typography variant="caption" display="block">
                  {uploadState.progress.processedFiles} / {uploadState.progress.totalFiles} files • 
                  {uploadState.progress.successfulUploads} successful • 
                  {uploadState.progress.failedUploads} failed
                </Typography>
              </Box>
            )}
            
            {/* Combined Statistics */}
            <Box display="flex" gap={1} flexWrap="wrap" mt={2}>
              <Chip 
                label={`${combinedStats.totalPending} pending`}
                size="small"
                color="default"
                variant="outlined"
              />
              <Chip 
                label={`${combinedStats.totalUploading} uploading`}
                size="small"
                color="primary"
              />
              <Chip 
                label={`${combinedStats.totalCompleted} completed`}
                size="small"
                color="success"
              />
              <Chip 
                label={`${combinedStats.totalFailed} failed`}
                size="small"
                color="error"
              />
            </Box>
            
            {/* Breakdown by source */}
            {(files.length > 0 || backgroundUploads.length > 0) && (
              <Box mt={2} pt={2} borderTop="1px solid rgba(0, 0, 0, 0.1)">
                <Typography variant="caption" color="textSecondary" gutterBottom>
                  Breakdown:
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap" mt={0.5}>
                  {files.length > 0 && (
                    <Typography variant="caption">
                      <strong>Local:</strong> {combinedStats.localPending} pending, {combinedStats.localUploading} uploading, {combinedStats.localCompleted} completed, {combinedStats.localFailed} failed
                    </Typography>
                  )}
                  {backgroundUploads.length > 0 && (
                    <Typography variant="caption">
                      <strong>Background:</strong> {combinedStats.bgPending} pending, {combinedStats.bgUploading} uploading, {combinedStats.bgCompleted} completed, {combinedStats.bgFailed} failed
                    </Typography>
                  )}
                  {uploadState.isUploading && uploadState.progress && (
                    <Typography variant="caption">
                      <strong>Chunked:</strong> {uploadState.progress.processedFiles}/{uploadState.progress.totalFiles} confirmed completed
                    </Typography>
                  )}
                </Box>
              </Box>
            )}
            
            {/* Retry All Failed Button */}
            {combinedStats.totalFailed > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={retryAllFailedFiles}
                sx={{ mt: 2 }}
                startIcon={<PlayArrowIcon />}
                fullWidth
              >
                Retry All Failed ({combinedStats.totalFailed})
              </Button>
            )}
          </Paper>
        )}

        {/* Background Uploads List - Show individual background uploads */}
        {displayedBackgroundUploads.length > 0 && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Background Uploads ({displayedBackgroundUploads.length})
            </Typography>
            {displayedBackgroundUploads.length > 100 && <Box sx={{display:'flex', gap:1}}>
              <Button disabled={backgroundPage === 0} onClick={() => setBackgroundPage(page => page - 1)}>Previous background files</Button>
              <Typography>Background page {backgroundPage + 1} of {Math.ceil(displayedBackgroundUploads.length / 100)}</Typography>
              <Button disabled={(backgroundPage + 1) * 100 >= displayedBackgroundUploads.length} onClick={() => setBackgroundPage(page => page + 1)}>Next background files</Button>
            </Box>}
            <List dense>
              {displayedBackgroundUploads.slice(backgroundPage * 100, (backgroundPage + 1) * 100).map((upload) => {
                const file = fileByUploadId.get(upload.id);
                const fileName = file?.file.name || upload.name || 'Unknown file';
                const fileSize = file?.file.size || upload.size || 0;
                
                return (
                  <ListItem key={upload.id} sx={{ px: 0 }}>
                    <ListItemIcon>
                      {upload.status === 'completed' ? (
                        <CheckCircleIcon color="success" />
                      ) : upload.status === 'error' ? (
                        <ErrorIcon color="error" />
                      ) : (
                        <UploadIcon color="primary" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Typography variant="body2" noWrap>
                            {fileName}
                          </Typography>
                          {upload.status === 'uploading' || upload.status === 'processing' ? (
                            <Box mt={0.5}>
                              <LinearProgress 
                                variant="determinate" 
                                value={upload.progress || 0} 
                                sx={{ height: 4, borderRadius: 2, mb: 0.5 }}
                              />
                              <Typography variant="caption" color="textSecondary">
                                {upload.progress || 0}% • {upload.speed ? `${(upload.speed / 1024 / 1024).toFixed(2)} MB/s` : ''}
                                {upload.size && upload.progress ? ` • ${((upload.size * upload.progress / 100) / 1024 / 1024).toFixed(2)} MB / ${(upload.size / 1024 / 1024).toFixed(2)} MB` : fileSize ? ` • ${(fileSize / 1024 / 1024).toFixed(2)} MB` : ''}
                              </Typography>
                            </Box>
                          ) : upload.status === 'completed' ? (
                            <Typography variant="caption" color="success.main">
                              Completed • {fileSize ? `${(fileSize / 1024 / 1024).toFixed(2)} MB` : ''}
                            </Typography>
                          ) : upload.status === 'error' ? (
                            <Typography variant="caption" color="error.main">
                              Failed: {upload.error || 'Unknown error'}
                            </Typography>
                          ) : (
                            <Typography variant="caption" color="textSecondary">
                              Pending • {fileSize ? `${(fileSize / 1024 / 1024).toFixed(2)} MB` : ''}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <Box display="flex" gap={0.5}>
                      {upload.status === 'error' && (
                        <IconButton 
                          size="small" 
                          onClick={() => {
                            const retainedFile = getRetainedUploadFile(upload.id);
                            if (!retainedFile) return;
                            const linked = files.find(f => f.uploadId === upload.id);
                            dispatch(removeUpload(upload.id));
                            if (linked) {
                              setFiles(prev =>
                                prev.map(f =>
                                  f.id === linked.id
                                    ? {
                                        ...f,
                                        uploadId: undefined,
                                        status: 'pending' as UploadStatus,
                                        progress: {
                                          ...f.progress,
                                          status: 'pending' as UploadStatus,
                                          progress: 0,
                                          error: undefined
                                        },
                                        error: undefined
                                      }
                                    : f
                                )
                              );
                            }
                            dispatch(
                              uploadFileBackground({
                                file: retainedFile,
                                folderId: upload.folderId,
                                clientFileId: linked?.id
                              })
                            );
                          }}
                          title="Retry upload"
                          color="primary"
                        >
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      )}
                      {(upload.status === 'uploading' || upload.status === 'processing' || upload.status === 'pending') && (
                        <IconButton 
                          size="small" 
                          onClick={() => dispatch(cancelBackgroundUpload(upload.id))}
                          title="Cancel upload"
                          color="error"
                        >
                          <StopIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </ListItem>
                );
              })}
            </List>
          </Paper>
        )}

        {uploadState.progress && (
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Upload Progress
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={uploadState.progress.progress} 
              sx={{ mb: 1 }}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="body2">
                  Progress: {uploadState.progress.progress}%
                </Typography>
                <Typography variant="body2">
                  Files: {uploadState.progress.processedFiles} / {uploadState.progress.totalFiles}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="success.main">
                  Success: {uploadState.progress.successfulUploads}
                </Typography>
                <Typography variant="body2" color="error.main">
                  Failed: {uploadState.progress.failedUploads}
                </Typography>
              </Grid>
            </Grid>
            {uploadState.currentBatch > 0 && (
              <Typography variant="body2" color="text.secondary">
                Batch: {uploadState.currentBatch} (Processing {uploadState.batchSize} files)
              </Typography>
            )}
          </Paper>
        )}

        <Box sx={{ mb: 3 }}>
          <Paper
            {...getRootProps()}
            sx={{
              border: '2px dashed',
              borderColor: isDragActive ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 4,
              textAlign: 'center',
              cursor: 'pointer',
              bgcolor: isDragActive ? 'rgba(0, 0, 0, 0.04)' : 'transparent',
              transition: 'all 0.2s ease',
              '&:hover': {
                bgcolor: 'rgba(0, 0, 0, 0.04)',
                borderColor: 'primary.main'
              }
            }}
          >
            {uploadType === 'files' ? (
              <>
                <InsertDriveFileIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  {isDragActive ? 'Drop files here' : 'Drag & Drop Files'}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  Drag files from your computer or click to select
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  <strong>Supported:</strong> Any file type (Max: {formatFileSize(maxFileSize)} per file)
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handleFileSelectClick}
                    disabled={isProcessing}
                    startIcon={<UploadIcon />}
                  >
                    Select Files
                  </Button>
                </Box>
              </>
            ) : (
              <>
                <FolderIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  {isDragActive ? 'Drop folder here' : 'Drag & Drop Folder'}
                </Typography>
                <Typography variant="body1" color="textSecondary" gutterBottom>
                  Drag entire folder from your computer or click to select
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  <strong>Important:</strong> Select the entire folder to avoid browser hanging
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                  <strong>Supported:</strong> Any file type (Max: {formatFileSize(maxFileSize)} per file)
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Large folders:</strong> Uploads use a bounded queue; keep this tab open until completion
                </Typography>
                <Box mt={2}>
                  <Button
                    variant="outlined"
                    onClick={handleFileSelectClick}
                    disabled={isProcessing}
                    startIcon={<FolderIcon />}
                  >
                    Select Folder
                  </Button>
                </Box>
              </>
            )}
          </Paper>
          
          {/* Hidden file input for files */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) {
                processFilesInChunks(Array.from(e.target.files));
              }
            }}
          />
          
          {/* Hidden folder input */}
          <input
            ref={folderInputRef}
            type="file"
            {...({ webkitdirectory: '', directory: '', multiple: true } as any)}
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files) {
                processFilesInChunks(Array.from(e.target.files));
              }
            }}
          />
        </Box>

        {files.length > 0 && (
          <Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">
                Files ({files.length})
              </Typography>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Chip 
                  label={`${pendingCount} pending`} 
                  color="default" 
                  size="small"
                  variant={combinedStats.totalPending > 0 ? "filled" : "outlined"}
                />
                <Chip 
                  label={`${uploadingCount} uploading`} 
                  color="primary" 
                  size="small"
                  variant={combinedStats.totalUploading > 0 ? "filled" : "outlined"}
                />
                <Chip 
                  label={`${completedCount} completed`} 
                  color="success" 
                  size="small"
                  variant={combinedStats.totalCompleted > 0 ? "filled" : "outlined"}
                />
                <Chip 
                  label={`${errorCount} failed`} 
                  color="error" 
                  size="small"
                  variant={combinedStats.totalFailed > 0 ? "filled" : "outlined"}
                />
                {combinedStats.totalFiles > 0 && (
                  <Chip 
                    label={`Total: ${combinedStats.totalFiles}`} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            <Paper sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {files.slice(filePage * 100, (filePage + 1) * 100).map((file) => (
                  <UploadFileRow key={file.id} file={file} upload={file.uploadId ? backgroundById.get(file.uploadId) : undefined}
                    sizeLabel={formatFileSize(file.file.size)} onRemove={removeFile} onRetry={retryQueuedFile} />
                ))}
                {files.length > 100 && (
                  <ListItem>
                    <ListItemText
                      primary={`... and ${files.length - 100} more files`}
                      secondary="Use the page controls to view all selected files"
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Box sx={{ flexGrow: 1 }} />
        
        {/* Always show Cancel button if there are active uploads */}
        {(uploadState.isUploading || isProcessing) && (
          <Button
            onClick={handleCancelClick}
            color="error"
            startIcon={<StopIcon />}
          >
            Cancel
          </Button>
        )}
        
        {/* Show Pause/Resume button when chunked upload is active */}
        {uploadState.isUploading && (
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleUploadClick(); // No parameter needed for pause/resume
            }}
            variant="contained"
            startIcon={uploadState.isPaused ? <PlayArrowIcon /> : <PauseIcon />}
          >
            {uploadState.isPaused ? 'Resume' : 'Pause'}
          </Button>
        )}
        
        {/* Upload Button - Always visible when files are selected and not actively uploading */}
        {files.length > 0 && !uploadState.isUploading && (
        <Button
          onClick={(e) => {
            e.preventDefault();
            const filesReadyToUpload = files.filter(f => 
              f.status === 'pending' || (f.status === 'error' && f.error && !f.error.includes('File size exceeds'))
            );
            console.log('[UPLOAD BUTTON] Clicked, using background upload');
            console.log('[UPLOAD BUTTON] Files ready to upload:', filesReadyToUpload.length, filesReadyToUpload.map(f => ({ name: f.file.name, status: f.status })));
            handleUploadClick(true); // Pass true directly to force background upload
          }}
          variant="contained"
          color="primary"
          disabled={(() => {
            // Check if there are any files ready to upload (pending or recoverable errors)
            const filesReadyToUpload = files.filter(f => 
              f.status === 'pending' || (f.status === 'error' && f.error && !f.error.includes('File size exceeds'))
            );
            
            // Disable if no files are ready to upload
            if (filesReadyToUpload.length === 0) {
              console.log('[UPLOAD BUTTON] Disabled: No files ready to upload');
              return true;
            }
            
            // Disable if any files are actively uploading (not pending)
            const hasActiveUploads = files.some(f => {
              if (f.status === 'uploading') return true;
              if (f.uploadId) {
                const bgUpload = backgroundById.get(f.uploadId!);
                if (bgUpload && (bgUpload.status === 'uploading' || bgUpload.status === 'processing')) {
                  return true;
                }
              }
              return false;
            });
            
            if (hasActiveUploads) {
              console.log('[UPLOAD BUTTON] Disabled: Has active uploads');
            } else {
              console.log('[UPLOAD BUTTON] Enabled: Ready to upload');
            }
            
            return hasActiveUploads;
          })()}
          startIcon={<CloudUploadIcon />}
        >
          {uploadType === 'folders' ? `Upload Folder` : `Upload Files`} {files.length > 0 ? `(${files.length})` : ''}
        </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SimpleFolderUploadDialog; 